import React from 'react';

import { Add as AddIcon, BorderColor as BorderColorIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber } from '@clearverify/utils';

import { CompanyType, type EmployerTableItemFragment } from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import {
  Table,
  TableColumn,
  TableExcelColumn,
  TableFilter,
  TableRowAction,
  TableSelectedRowId,
  TableThemes,
  TableTopAction,
} from 'src/components/table';
import { COMPANY_SEARCH_CATEGORIES } from 'src/constants/company';
import type { ModalId } from 'src/constants/modals';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { buildEmployerDetailsUrl, getEmployerStatus } from 'src/utils/employer';

type EmployerInnerTableClearVerifyStaffProps = {
  employersList: EmployerTableItemFragment[];
  employersCount: number;
  loading: boolean;
  inviteModalId?: ModalId;
  isDetail?: boolean;
  tableTheme?: TableThemes;
  pagination: PaginationContextValue;
  onRefresh?: () => Promise<void>;
};

export const EmployerInnerTableClearVerifyStaff: React.FC<
  EmployerInnerTableClearVerifyStaffProps
> = ({
  employersList,
  employersCount,
  loading,
  inviteModalId,
  isDetail = false,
  tableTheme = TableThemes.default,
  pagination,
  onRefresh,
}) => {
  const navigate = useNavigate();

  const { openModal } = useModal();
  const { setSearchValue, searchValue, setSearchCategory, searchCategory } = useSearch();
  const { setSortOption } = useFilterStorage();

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue, searchCategory]);

  const [selectedIds, setSelectedIds] = React.useState<TableSelectedRowId[]>([]);

  const rowActions = React.useMemo(
    (): Array<TableRowAction<EmployerTableItemFragment>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: employer => {
          navigate(buildEmployerDetailsUrl(employer.id));
        },
      },
    ],
    [navigate],
  );

  const topActions = React.useMemo(
    (): Array<TableTopAction> =>
      inviteModalId
        ? [
            {
              actionName: 'Add',
              show: true,
              icon: <AddIcon />,
              onAction: () => openModal(inviteModalId),
              render() {
                return (
                  <Button
                    key={this.actionName}
                    variant="contained"
                    startIcon={this.icon}
                    onClick={this.onAction}
                  >
                    New
                  </Button>
                );
              },
            },
          ]
        : [],
    [inviteModalId, openModal],
  );

  const columns = React.useMemo(
    (): Array<TableColumn<EmployerTableItemFragment>> => [
      {
        title: 'Name',
        key: 'displayName',
        sortable: true,
        render: (_valueByDataPath, employer) => {
          return (
            <ClickableLabel
              type="link"
              to={buildEmployerDetailsUrl(employer.id)}
              label={employer.displayName}
              logoUrl={employer.logo?.downloadUrl}
              withoutLogo={isDetail}
              badgeContent={employer.setLiveAt ? 'Live' : null}
            />
          );
        },
      },
      {
        title: 'PEO',
        key: 'peo.displayName',
        sortable: true,
        render: (_valueByDataPath, employer) =>
          employer.controlledBy === CompanyType.Peo ? (
            <DemoPrivacyText>{employer.peo?.displayName}</DemoPrivacyText>
          ) : (
            '–'
          ),
      },
      {
        title: 'PEO PP',
        key: 'peoPayrollProvider.displayName',
        sortable: true,
        render: (_valueByDataPath, employer) =>
          employer.controlledBy === CompanyType.PeoPayrollProvider ? (
            <DemoPrivacyText>{employer.peoPayrollProvider?.displayName}</DemoPrivacyText>
          ) : (
            '–'
          ),
      },
      {
        title: 'Contact Email',
        key: 'contactEmail',
        sortable: true,
        render: (_valueByDataPath, employer) => {
          return <DemoPrivacyText>{employer.contactEmail}</DemoPrivacyText>;
        },
      },
      {
        title: 'Contact Phone',
        key: 'contactPhone',
        render: (_valueByDataPath, employer) => {
          return (
            <DemoPrivacyText>{formatPhoneNumber(employer.contactPhoneNumber)}</DemoPrivacyText>
          );
        },
      },
      {
        title: 'Status',
        key: 'status',
        render: (_valueByDataPath, employer) => {
          return <UserStatusChip userStatus={getEmployerStatus(employer)} />;
        },
      },
    ],
    [isDetail],
  );

  return (
    <Table
      dataSource={employersList}
      rowCount={employersCount}
      columns={columns}
      rowActions={rowActions}
      topActions={topActions}
      onSearch={setSearchValue}
      onSearchCategorySelect={setSearchCategory}
      pagination={pagination}
      filters={filters}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      excelColumns={excelColumns}
      loading={loading}
      showDownloadIcon={false}
      tableTheme={tableTheme}
      onRefresh={onRefresh}
      onSort={setSortOption}
      searchCategories={COMPANY_SEARCH_CATEGORIES}
    />
  );
};

const excelColumns: Array<TableExcelColumn<EmployerTableItemFragment>> = [
  {
    header: 'Name',
    key: 'displayName',
  },
  {
    header: 'Contact Email',
    key: 'contactEmail',
  },
  {
    header: 'Contact Phone',
    key: 'contactPhone',
    render: (_valueByKey, employer) => {
      return formatPhoneNumber(employer.contactPhoneNumber);
    },
  },
  {
    header: 'User Status',
    key: 'status',
    render: (_valueByKey, employer) => {
      return getEmployerStatus(employer);
    },
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
